"use client";

import { useState } from "react";
import { Arrow } from "./Icon";

export interface ContactFormLabels {
  first_name: string;
  first_name_placeholder: string;
  last_name: string;
  last_name_placeholder: string;
  company: string;
  company_placeholder: string;
  position: string;
  position_placeholder: string;
  email: string;
  email_placeholder: string;
  mobile: string;
  mobile_placeholder: string;
  industry: string;
  industry_placeholder: string;
  other_label: string;
  help_section_title: string;
  help_options: string[];
  message: string;
  message_placeholder: string;
  message_help: string;
  submit_label: string;
  submit_sending: string;
  security_note: string;
  success_title: string;
  success_subtitle: string;
  success_again_label: string;
  error_generic: string;
}

export default function ContactForm({
  industries,
  labels,
}: {
  industries: { slug: string; title: string }[];
  labels: ContactFormLabels;
}) {
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [error, setError] = useState("");
  const [selectedHelp, setSelectedHelp] = useState<string[]>([]);

  function toggleHelp(opt: string) {
    setSelectedHelp((prev) =>
      prev.includes(opt) ? prev.filter((o) => o !== opt) : [...prev, opt]
    );
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const form = e.currentTarget;
    const fd = new FormData(form);
    const data = {
      full_name: `${fd.get("first_name")} ${fd.get("last_name")}`.trim(),
      job_title: fd.get("position"),
      company: fd.get("company"),
      industry: fd.get("industry"),
      email: fd.get("email"),
      phone: fd.get("mobile"),
      message: [
        selectedHelp.length ? `How can we help: ${selectedHelp.join(", ")}` : "",
        "",
        fd.get("message"),
      ].filter(Boolean).join("\n"),
    };
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || labels.error_generic);
      }
      setStatus("ok");
      form.reset();
      setSelectedHelp([]);
    } catch (err: any) {
      setStatus("error");
      setError(err.message);
    }
  }

  if (status === "ok") {
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-8 text-center dark:border-emerald-800/60 dark:bg-emerald-950/40">
        <h3 className="text-lg font-bold text-navy dark:text-emerald-100">{labels.success_title}</h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-emerald-200/80">{labels.success_subtitle}</p>
        <button onClick={() => setStatus("idle")} className="mt-5 text-sm font-semibold text-brand-red dark:text-brand-red-bright">
          {labels.success_again_label}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      {/* Name row */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field name="first_name" label={labels.first_name} placeholder={labels.first_name_placeholder} required />
        <Field name="last_name" label={labels.last_name} placeholder={labels.last_name_placeholder} required />
      </div>

      {/* Company + Position */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field name="company" label={labels.company} placeholder={labels.company_placeholder} required />
        <Field name="position" label={labels.position} placeholder={labels.position_placeholder} />
      </div>

      {/* Email + Mobile */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field name="email" label={labels.email} type="email" placeholder={labels.email_placeholder} required />
        <Field name="mobile" label={labels.mobile} type="tel" placeholder={labels.mobile_placeholder} />
      </div>

      {/* Industry */}
      <div>
        <Label>{labels.industry} <Req /></Label>
        <select name="industry" required defaultValue="" className="input">
          <option value="" disabled>{labels.industry_placeholder}</option>
          {industries.map((i) => (
            <option key={i.slug} value={i.title}>{i.title}</option>
          ))}
          <option value="Other">{labels.other_label}</option>
        </select>
      </div>

      {/* How Can We Help? checkbox group + Message column layout */}
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <h4 className="mb-3 text-sm font-bold text-navy">{labels.help_section_title}</h4>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {labels.help_options.map((opt) => (
              <label key={opt} className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={selectedHelp.includes(opt)}
                  onChange={() => toggleHelp(opt)}
                  className="mt-0.5 h-4 w-4 accent-brand-red"
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label>{labels.message}</Label>
          <p className="mb-1.5 text-xs text-gray-500">{labels.message_help}</p>
          <textarea
            name="message"
            rows={6}
            placeholder={labels.message_placeholder}
            className="input resize-none"
          />
        </div>
      </div>

      {status === "error" && <p className="text-sm text-brand-red">{error}</p>}

      <button type="submit" disabled={status === "sending"} className="btn-red w-full disabled:opacity-60">
        {status === "sending" ? labels.submit_sending : <>{labels.submit_label} <Arrow /></>}
      </button>

      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-gray-500">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="4" y="11" width="16" height="10" rx="1.5" />
          <path d="M8 11V8a4 4 0 0 1 8 0v3" />
        </svg>
        {labels.security_note}
      </p>
    </form>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <label className="mb-1.5 block text-sm font-semibold text-navy">{children}</label>;
}
function Req() {
  return <span className="text-brand-red">*</span>;
}
function Field({
  name, label, type = "text", required = false, placeholder,
}: {
  name: string; label: string; type?: string; required?: boolean; placeholder?: string;
}) {
  return (
    <div>
      <Label>{label} {required && <Req />}</Label>
      <input name={name} type={type} required={required} placeholder={placeholder} className="input" />
    </div>
  );
}
